import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGql, Skeleton } from '@ah/design';
import { Header, BottomSheet } from '@ah/design/mobile';
import {
  INITIATIVE_BY_SLUG_Q,
  REGISTER_INITIATIVE_INTEREST_M,
  WITHDRAW_INITIATIVE_INTEREST_M,
  type Initiative,
} from '../shared/index.ts';
import { InitiativeCard } from './InitiativeCard.tsx';
import { InitiativeForm, type InitiativeFormValues } from './InitiativeForm.tsx';

export function InitiativeDetail() {
  const { slug } = useParams<{ slug: string }>();
  const gql = useGql();
  const qc = useQueryClient();
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['initiatives', 'detail', slug],
    queryFn: async () => {
      const res = await gql.request<{ initiative: Initiative | null }>(INITIATIVE_BY_SLUG_Q, {
        slug,
      });
      return res.initiative;
    },
    enabled: !!slug,
  });

  const enrolled = data?.viewerStatus === 'interested';
  const hasFormFields = (data?.formFields.length ?? 0) > 0;

  const initialValues = useMemo<InitiativeFormValues | undefined>(() => {
    if (!data?.viewerFormData) return undefined;
    try {
      const parsed = JSON.parse(data.viewerFormData) as Record<string, unknown>;
      const out: InitiativeFormValues = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (Array.isArray(v)) out[k] = v.filter((x): x is string => typeof x === 'string');
        else if (typeof v === 'string') out[k] = v;
      }
      return out;
    } catch {
      return undefined;
    }
  }, [data?.viewerFormData]);

  const enroll = useMutation({
    mutationFn: async (values: InitiativeFormValues | null) => {
      if (!data) return;
      const formData = values && data.formFields.length > 0 ? JSON.stringify(values) : null;
      await gql.request(REGISTER_INITIATIVE_INTEREST_M, { slug: data.slug, formData });
    },
    onSuccess: () => {
      setSheetOpen(false);
      void qc.invalidateQueries({ queryKey: ['initiatives'] });
      void refetch();
    },
  });

  const withdraw = useMutation({
    mutationFn: async () => {
      if (!data) return;
      await gql.request(WITHDRAW_INITIATIVE_INTEREST_M, { slug: data.slug });
    },
    onSuccess: () => {
      setSheetOpen(false);
      void qc.invalidateQueries({ queryKey: ['initiatives'] });
      void refetch();
    },
  });

  function handlePrimary() {
    if (!data) return;
    if (enrolled) {
      if (hasFormFields) setSheetOpen(true);
      else withdraw.mutate();
    } else {
      if (hasFormFields) setSheetOpen(true);
      else enroll.mutate(null);
    }
  }

  const primaryLabel = enrolled
    ? hasFormFields
      ? 'Edit enrolment'
      : withdraw.isPending
        ? 'Cancelling…'
        : 'Cancel enrolment'
    : hasFormFields
      ? 'Enrol'
      : enroll.isPending
        ? 'Enrolling…'
        : 'Enrol';

  const primaryClass = enrolled && !hasFormFields
    ? 'border border-border bg-card text-foreground hover:bg-accent'
    : 'bg-primary text-primary-foreground hover:bg-primary/90';

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Initiative" backTo="/initiatives" />
      <div className="flex-1 overflow-y-auto px-3 py-3 pb-4 space-y-4">
        {isLoading ? (
          <DetailSkeleton />
        ) : error || !data ? (
          <div className="text-center py-16">
            <p className="text-sm text-destructive mb-3">Could not load this initiative.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent"
            >
              Retry
            </button>
          </div>
        ) : (
          <InitiativeCard initiative={data} />
        )}
      </div>

      {data && !isLoading && !error && (
        <div
          className="shrink-0 border-t border-border bg-background/90 backdrop-blur px-3 pt-3"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
        >
          <button
            type="button"
            disabled={enroll.isPending || withdraw.isPending}
            onClick={handlePrimary}
            className={`w-full flex items-center justify-center h-12 rounded-full font-semibold text-sm transition-all active:scale-[0.98] disabled:opacity-60 ${primaryClass}`}
          >
            {primaryLabel}
          </button>
        </div>
      )}

      {data && hasFormFields && (
        <BottomSheet
          open={sheetOpen}
          onClose={() => {
            if (!enroll.isPending && !withdraw.isPending) setSheetOpen(false);
          }}
          title={data.title}
          subtitle={enrolled ? 'Edit your enrolment' : 'Enrol'}
          fullHeight
          footer={
            enrolled ? (
              <button
                type="button"
                disabled={withdraw.isPending}
                onClick={() => withdraw.mutate()}
                className="w-full h-11 rounded-full text-sm font-medium text-destructive hover:bg-destructive/5 disabled:opacity-60"
              >
                {withdraw.isPending ? 'Cancelling…' : 'Cancel enrolment'}
              </button>
            ) : null
          }
        >
          <InitiativeForm
            initiative={data}
            initialValues={initialValues}
            submitting={enroll.isPending}
            submitLabel={enrolled ? 'Save changes' : 'Enrol'}
            onSubmit={(values) => enroll.mutate(values)}
          />
        </BottomSheet>
      )}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="w-full aspect-video rounded-2xl" />
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-20 w-full" />
    </div>
  );
}
